import { useState } from 'react';

import { SettingsPageFrame } from '@eluve/blocks';
import { Button, Caption, Stack, Textarea, useToast } from '@eluve/components';
import { useBeforeUnload } from '@eluve/utility-hooks';

import { useChatSettings } from './useChatSettings';

const CHAT_EXAMPLE_INSTRUCTIONS = [
  'Remove parts about...',
  'Include discussion about...',
  'Make notes more concise',
  'Add more details in the plan section',
];

export const ChatCustomInstructionsPage = () => {
  const { customInstructions, saveInstructions } = useChatSettings();
  const toast = useToast();

  const [value, setValue] = useState(customInstructions);
  const [isSaving, setIsSaving] = useState(false);

  const isDirty = value !== customInstructions;
  useBeforeUnload(isDirty);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveInstructions(value);
    } catch {
      toast.error('Failed to save custom instructions');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsPageFrame
      title="Chat Custom Instructions"
      parentTitle="Chat Preferences"
      backTo=".."
      description="Customize your notes by providing specific guidelines. Try to be as clear and concise as possible for best results."
    >
      <Stack gap={4} align="stretch">
        <div className="overflow-hidden rounded-md border border-border-primary bg-bg-primary focus-within:ring-1 focus-within:ring-border-secondary">
          <Textarea
            placeholder="Type your instructions here"
            className="min-h-55 resize-none border-0 bg-transparent shadow-none ring-0 ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>

        <Stack direction="horizontal" justify="end" align="center" gap={2}>
          <Button
            variant="ghost"
            size="sm"
            disabled={!isDirty || isSaving}
            onClick={() => setValue(customInstructions)}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!isDirty || isSaving}
            loading={isSaving}
            onClick={handleSave}
          >
            Save
          </Button>
        </Stack>
      </Stack>

      <Stack gap={2} align="start">
        <Caption
          size="3xs"
          weight="bold"
          color="supporting"
          className="block tracking-[2px]"
        >
          EXAMPLES
        </Caption>
        <Stack gap={2} align="start">
          {CHAT_EXAMPLE_INSTRUCTIONS.map((example) => (
            <Button
              key={example}
              variant="outline"
              size="sm"
              onClick={() => setValue(example)}
            >
              {example}
            </Button>
          ))}
        </Stack>
      </Stack>
    </SettingsPageFrame>
  );
};
